import React, { createContext, useContext, useState, useEffect } from 'react';
import { subscriptionApi } from '../services/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default free plan data for unverified or error states
  const defaultSubscription = {
    has_active_subscription: false,
    user_plan: 'free',
    can_use_features: true
  };

  const defaultUsage = {
    plan: { name: 'Free Plan', slug: 'free' },
    resumes: { used: 0, limit: 1, remaining: 1, unlimited: false },
    ai_generations: { used: 0, limit: 5, remaining: 5, unlimited: false },
    cover_letters: { used: 0, limit: 10, remaining: 10, unlimited: false },
    chat_messages_today: { used: 0, limit: 10, remaining: 10, unlimited: false },
    job_search: {
      basic_search: true,
      remote_search: true,
      advanced_search: false,
      advanced_matching: false
    }
  };

  // Fetch subscription and usage data
  const fetchSubscriptionData = async () => {
    // No user - clear everything
    if (!currentUser) {
      setSubscription(null);
      setUsage(null);
      setLoading(false);
      return;
    }

    // Check if user's email is verified (skip for Google users)
    const isGoogleUser = currentUser.providerData?.some(
      provider => provider.providerId === 'google.com'
    );
    
    // If email not verified and not a Google user, use defaults without API call
    if (!currentUser.emailVerified && !isGoogleUser) {
      setSubscription(defaultSubscription);
      setUsage(defaultUsage);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch both subscription and usage in parallel
      const [subscriptionData, usageData] = await Promise.all([
        subscriptionApi.getCurrentSubscription(),
        subscriptionApi.getUsageStats()
      ]);

      setSubscription(subscriptionData);
      setUsage(usageData);
    } catch (err) {
      console.error('Error fetching subscription/usage:', err);
      setError(err);
      // Set default free plan on error
      setSubscription(defaultSubscription);
      setUsage(defaultUsage);
    } finally {
      setLoading(false);
    }
  };

  // Refresh subscription data
  const refreshSubscription = async () => {
    await fetchSubscriptionData();
  };

  // Check if user can perform an action
  const canGenerateCV = () => {
    if (!usage || !usage.resumes) return false;
    return usage.resumes.remaining > 0 || usage.resumes.unlimited;
  };

  const canUseAIGeneration = () => {
    if (!usage || !usage.ai_generations) return false;
    return usage.ai_generations.remaining > 0 || usage.ai_generations.unlimited;
  };

  const canGenerateCoverLetter = () => {
    if (!usage || !usage.cover_letters) return false;
    return usage.cover_letters.remaining > 0 || usage.cover_letters.unlimited;
  };

  const canUseChat = () => {
    if (!usage || !usage.chat_messages_today) return false;
    return usage.chat_messages_today.remaining > 0 || usage.chat_messages_today.unlimited;
  };

  const canUseAdvancedSearch = () => {
    if (!usage || !usage.job_search) return false;
    return usage.job_search.advanced_search === true;
  };

  const canUseAdvancedMatching = () => {
    if (!usage || !usage.job_search) return false;
    return usage.job_search.advanced_matching === true;
  };

  // Get remaining counts
  const getRemainingResumes = () => {
    if (!usage || !usage.resumes) return 0;
    if (usage.resumes.unlimited) return Infinity;
    return usage.resumes.remaining;
  };

  const getRemainingAIGenerations = () => {
    if (!usage || !usage.ai_generations) return 0;
    if (usage.ai_generations.unlimited) return Infinity;
    return usage.ai_generations.remaining;
  };

  const getRemainingCoverLetters = () => {
    if (!usage || !usage.cover_letters) return 0;
    if (usage.cover_letters.unlimited) return Infinity;
    return usage.cover_letters.remaining;
  };

  const getRemainingChatMessages = () => {
    if (!usage || !usage.chat_messages_today) return 0;
    if (usage.chat_messages_today.unlimited) return Infinity;
    return usage.chat_messages_today.remaining;
  };

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    return subscription?.has_active_subscription || false;
  };

  // Check if user is in trial period
  const isInTrial = () => {
    return subscription?.is_trial || false;
  };

  // Get current plan name
  const getCurrentPlan = () => {
    return subscription?.user_plan || 'free';
  };

  // Get days until renewal
  const getDaysUntilRenewal = () => {
    return subscription?.days_until_renewal || null;
  };

  // Cancel subscription
  const cancelSubscription = async (cancelImmediately = false, reason = null) => {
    try {
      const result = await subscriptionApi.cancelSubscription(cancelImmediately, reason);
      await refreshSubscription(); 
      return result;
    } catch (err) {
      throw err;
    }
  };

  // Fetch subscription data when user changes
  useEffect(() => {
    fetchSubscriptionData();
  }, [currentUser]);

  const value = {
    // State
    subscription,
    usage,
    loading,
    error,
    
    // Actions
    refreshSubscription,
    cancelSubscription,
    
    // Helper methods
    canGenerateCV,
    canUseAIGeneration,
    canGenerateCoverLetter,
    canUseChat,
    canUseAdvancedSearch,
    canUseAdvancedMatching,
    getRemainingResumes,
    getRemainingAIGenerations,
    getRemainingCoverLetters,
    getRemainingChatMessages,
    hasActiveSubscription,
    isInTrial,
    getCurrentPlan,
    getDaysUntilRenewal
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
